import { WorkOrderPaymentMethodEnum } from "@repo/graphql/gql/graphql";
import { Dispatch, SetStateAction, useEffect, useState } from "react";

type SelectablePaymentOption = {
  method: WorkOrderPaymentMethodEnum;
  available: boolean;
};

/**
 * Selects a payment method out of a checkout's `options`, mirroring
 * `useState`'s `[value, setValue]` shape. Picks the first available option
 * on load, and re-syncs whenever the currently selected option becomes
 * unavailable (e.g. after a refetch drops the wallet balance below the
 * total) while another option is still available — it never overrides an
 * explicit user pick as long as that pick stays available.
 */
export function useSelectedPaymentMethod<T extends SelectablePaymentOption>(
  options: T[],
): [
  WorkOrderPaymentMethodEnum | undefined,
  Dispatch<SetStateAction<WorkOrderPaymentMethodEnum | undefined>>,
] {
  const [selected, setSelected] = useState<
    WorkOrderPaymentMethodEnum | undefined
  >(undefined);

  useEffect(() => {
    if (options.length === 0) return;
    const current = options.find((option) => option.method === selected);
    if (current?.available) return;

    const firstAvailable = options.find((option) => option.available);
    if (selected === undefined) {
      setSelected((firstAvailable ?? options[0]).method);
      return;
    }
    if (firstAvailable) {
      setSelected(firstAvailable.method);
    }
  }, [options, selected]);

  return [selected, setSelected];
}
